import { Persona, ShiftTemplate } from "../../models";
import { PERSONA_LADDER, PROTO_PERSONAS } from "../prototype/personas-data";
import { USERS, EMPLOYEE_PHOTOS } from "../prototype/company-directory-data";

const DOW_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * The prototype stores a shift as `{ start, end, days: ['Mon', …] }`; the app
 * model uses one `{ weekday, start, end }` entry per day. Expand each raw shift
 * into per-weekday entries so every reader (schedule display, capacity,
 * out-of-hours) sees a consistent shape. Already-normalized entries pass through.
 */
function normalizeShifts(raw: unknown): ShiftTemplate[] {
  if (!Array.isArray(raw)) return [];
  const out: ShiftTemplate[] = [];
  for (const s of raw as { start?: string; end?: string; days?: unknown; weekday?: unknown }[]) {
    if (!s?.start || !s?.end) continue;
    if (Array.isArray(s.days)) {
      for (const d of s.days) {
        const wd = typeof d === "number" ? d : DOW_INDEX[d as string];
        if (wd != null) out.push({ weekday: wd, start: s.start, end: s.end });
      }
    } else if (typeof s.weekday === "number") {
      out.push({ weekday: s.weekday, start: s.start, end: s.end });
    }
  }
  return out;
}

/** personaId → portrait URL, joined through the company directory (USERS.personaId → EMPLOYEE_PHOTOS[userId]). */
const PHOTO_BY_PERSONA: Record<string, string> = Object.fromEntries(
  (USERS as { id: string; personaId: string | null }[])
    .filter((u) => u.personaId && (EMPLOYEE_PHOTOS as Record<string, string>)[u.id])
    .map((u) => [u.personaId as string, (EMPLOYEE_PHOTOS as Record<string, string>)[u.id]]),
);

/**
 * Company directory — mapped from the prototype PROTO_PERSONAS (source of truth).
 * Names, titles, role flags, and reporting lines come straight from the
 * prototype. scheduledHoursPerWeek defaults to 40 and shifts to [] where the
 * prototype persona does not specify them (matching its own fallback).
 */
export const PERSONAS_SEED: readonly Persona[] = Object.values(PROTO_PERSONAS as Record<string, any>).map(
  (p): Persona => ({
    id: p.id,
    name: p.name,
    title: p.title,
    initials: p.initials ?? "",
    photoUrl: PHOTO_BY_PERSONA[p.id] ?? null,
    tier: p.tier ?? "",
    function: p.function ?? "",
    growth: p.growth ?? "",
    isAdmin: !!p.isAdmin,
    isCEO: !!p.isCEO,
    isSupervisor: !!p.isSupervisor,
    hasCareerLadder: p.id in PERSONA_LADDER,
    reportsTo: p.reportsTo ?? null,
    directReports: Array.isArray(p.directReports) ? p.directReports : [],
    dottedLine: Array.isArray(p.dottedLine) ? p.dottedLine : undefined,
    teammate: p.teammate,
    mobilePrimary: p.mobilePrimary,
    notes: p.notes,
    trades: Array.isArray(p.trades) ? p.trades : [],
    hourlyWage: p.hourlyWage,
    positionId: p.positionId,
    scheduledHoursPerWeek: p.scheduledHoursPerWeek ?? 40,
    shifts: normalizeShifts(p.shifts),
  }),
);
